import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Divider, Button, FormGroup, FormControl, InputLabel, Select, MenuItem} from '@material-ui/core';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@material-ui/icons';
import {DatePicker} from '@material-ui/pickers';
import {Panel} from 'react-bootstrap';
import {isEmpty} from 'lodash/lang';
import moment from 'moment';
import {BlockUI, FormErrors} from 'components/common';
import {SecondaryNavigation} from 'components/Navigation';
import BulkStudentsCreationForm from './BulkStudentsCreationForm';
import {bulkCreateStudentsToClassroom} from 'api/subscriptions';
import {fetchOrders} from 'api/orders';
import {addFlashMessage} from 'actions/flash-message';
import {formatErrors} from 'actions/errors';
import {generateCSVFile, readExcelFile, getDefaultSubscriptionExpiryDate, formatDatetime} from 'utils/common';
import {isKluwellAdmin, isTeacher} from 'utils/permissions';

const initializeUserData = (userData) => {
  const data = {};
  const idxList = [];
  for (let [idx, user] of Object.entries(userData)) {
    idxList.push(parseInt(idx));
    data[`${idx}-firstName`] = user.firstName;
    data[`${idx}-lastName`] = user.lastName;
  }
  return {idxList, data};
};

const getUserLength = (userData) => {
  const len = Object.keys(userData).length / Object.keys(fieldNames).length;
  if (!Number.isInteger(len)) {
    throw Error('Invalid input of userData and fieldNames');
  }
  return len;
};

const userDataToList = (userData) => {
  const userList = [];
  const idxList = [];
  for (let idx = 0; idx < getUserLength(userData); ++idx) {
    idxList.push(idx);
    userList.push({
      firstName: userData[`${idx}-firstName`],
      lastName: userData[`${idx}-lastName`],
    });
  }
  return userList;
};

const fieldNames = {
  firstName: 'First Name',
  lastName: 'Last Name',
};

const textToFieldMaps = {
  'First Name': 'firstName',
  'Last Name': 'lastName',
};

const downloadBulkStudentsCreationTemplate = () =>
  generateCSVFile([Object.values(fieldNames)], 'bulk-student-creation-template.csv');

class BulkStudentsCreation extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    user: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      fileName: null,
      userData: {},
      fileInputKey: 0,
      errors: {},
      blockUIShown: false,
      orders: [],
      selectedOrderId: null,
    };
    this.fetchOrders = this.fetchOrders.bind(this);
    this.handleOrderChange = this.handleOrderChange.bind(this);
    this.initializeData = this.initializeData.bind(this);
    this.handleOpenFile = this.handleOpenFile.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleUserRemove = this.handleUserRemove.bind(this);
  }

  componentDidMount() {
    this.fetchOrders();
  }

  fetchOrders() {
    const {
      match: {
        params: {classroomId},
      },
    } = this.props;
    return fetchOrders(undefined, undefined, undefined, undefined, {classroomId, activeOnly: true}).then(
      ({records}) => {
        const orders = records.map((record) => ({
          ...record,
          name: `${record.name} (Order expires at: ${formatDatetime(record.expiryDate, false, 'MMMM Do, YYYY')})`,
        }));
        this.setState({
          orders,
          selectedOrderId: (orders[0] || {}).id || null,
        });
      },
    );
  }

  handleOrderChange(selectedOrderId) {
    this.setState({
      selectedOrderId,
    });
  }

  initializeData(data) {
    const {idxList, data: userData} = initializeUserData(data);
    this.idxList = idxList;
    this.setState({
      userData,
    });
  }

  handleOpenFile(e) {
    const {addFlashMessage} = this.props;
    const file = e.target.files[0];
    if (typeof file === 'undefined') {
      this.setState({
        userData: {},
        fileName: null,
      });
      return;
    }
    this.setState({
      fileName: file.name,
    });
    readExcelFile(file)
      .then((data) => {
        const userList = [];
        for (const rawUser of data) {
          const user = {};
          for (const [text, fieldName] of Object.entries(textToFieldMaps)) {
            user[fieldName] = rawUser[text];
          }
          userList.push(user);
        }
        return new Promise((resolve, reject) => {
          try {
            this.initializeData(userList, file);
          } catch (e) {
            reject(
              'There is something wrong in the file you provided. Please download and ' +
                'fill out the CSV file as per the rules demonstrated above',
            );
          }
        });
      })
      .catch((err) => addFlashMessage(err, 'error'));
  }

  handleFormSubmit(data) {
    const {selectedOrderId} = this.state;
    const {
      addFlashMessage,
      formatErrors,
      match: {
        params: {classroomId},
      },
    } = this.props;
    const {
      router: {
        history: {push},
      },
    } = this.context;
    this.setState({
      blockUIShown: true,
    });
    bulkCreateStudentsToClassroom({
      classroomId: parseInt(classroomId),
      orderId: selectedOrderId,
      users: userDataToList(data),
    })
      .then(() => {
        addFlashMessage('Students are created successfully!', 'success');
        this.setState(
          {
            blockUIShown: false,
          },
          () => push(`/classrooms/${classroomId}`),
        );
      })
      .catch((err) =>
        formatErrors(err).then((errors) => {
          this.setState({
            errors,
            blockUIShown: false,
          });
        }),
      );
  }

  handleUserRemove(idx) {
    const {userData} = this.state;
    const userList = userDataToList(userData).filter((user, index) => index !== idx);
    if (userList.length === 0) {
      this.setState((prevState) => ({
        fileInputKey: prevState.fileInputKey + 1,
      }));
    }
    this.initializeData(userList);
  }

  render() {
    const {fileName, userData, fileInputKey, errors, blockUIShown, orders, selectedOrderId} = this.state;
    const {
      user: me,
      match: {
        params: {classroomId},
      },
    } = this.props;
    return (
      <React.Fragment>
        <BlockUI shown={blockUIShown} />
        <SecondaryNavigation
          tabs={[
            {url: `/classrooms/${classroomId}`, label: 'Classroom Detail', isActive: false},
            {
              url: `/classrooms/${classroomId}/bulk-create-students`,
              label: 'Bulk Create Students',
              isActive: true,
            },
            ['kluwell_admin', 'teacher', 'school_coordinator'].includes(me.role) && {
              url: `/classrooms/${classroomId}/admin`,
              label: 'Student Reading Nights',
              isActive: false,
            },
          ].filter((v) => v)}
          hasBack
        />
        <div className="container content-box">
          <Panel>
            <h3 className="section-header">
              <div className="section-title">Bulk create students to classroom</div>
            </h3>
            <ol>
              <li>Select expiry date for subscriptions</li>
              <li>
                Click to download a sample spreadsheet:
                <p>
                  <Button variant="contained" color="primary" onClick={downloadBulkStudentsCreationTemplate}>
                    Download Template
                  </Button>
                  <span className="text-danger">Required field: First Name, Last Name</span>
                </p>
              </li>
              <li>Populate the template with students information.</li>
              <li>Upload your file below.</li>
            </ol>
            <br />
            <Divider light />
            <br />
            <FormGroup>
              <FormControl>
                <InputLabel htmlFor="bulk-subscription-order" shrink={!!selectedOrderId}>
                  Order
                </InputLabel>
                <Select
                  value={selectedOrderId || ''}
                  onChange={({target: {value}}) => this.handleOrderChange(value)}
                  inputProps={{
                    name: 'order',
                    id: 'bulk-subscription-order',
                  }}
                  renderValue={(orderId) => orders.find(({id}) => orderId === id).name}
                >
                  {orders.map(({id, name}) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormGroup>
            <br />
            <div>
              <input
                key={fileInputKey}
                id="choose-bulk-create-csv"
                type="file"
                className="hidden"
                onChange={this.handleOpenFile}
                tabIndex="-1"
                accept=".csv"
              />
              <Button htmlFor="choose-bulk-create-csv" component="label" variant="contained" color="secondary">
                Choose CSV File
              </Button>
              {fileName && <span className="small-margin">{fileName}</span>}
            </div>
          </Panel>
          {!isEmpty(selectedOrderId) && !isEmpty(userData) && (
            <Panel>
              <FormErrors errors={errors} />
              <BulkStudentsCreationForm
                fieldNames={fieldNames}
                idxList={this.idxList}
                initialValues={userData}
                onSubmit={this.handleFormSubmit}
                onUserRemove={this.handleUserRemove}
              />
            </Panel>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default connect(
  ({account}) => ({
    user: account.get('user').toJS(),
  }),
  {
    addFlashMessage,
    formatErrors,
  },
)(BulkStudentsCreation);
